import type {
  FetchFlagsInputForGrid,
  FlagCategory,
  FlagPriority,
  FlagQueryFilterForGrid,
  FlagStates,
  FlagTypes,
} from '@generated/graphql';
import dayjs from 'dayjs';
import type { ReconFlagSeverity } from './reconFlags.types';

const SEVERITY_TAB_VALUES = new Set<string>([
  'ALL',
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
]);

/** Coerce URL hash value into a supported severity tab. */
export function parseReconFlagsSeverityTab(
  stored: string,
): 'ALL' | ReconFlagSeverity {
  if (SEVERITY_TAB_VALUES.has(stored)) {
    return stored as 'ALL' | ReconFlagSeverity;
  }
  return 'ALL';
}

export type BuildReconFlagsGridFilterInput = {
  orgId: string | undefined;
  flagState: string;
  flagType: string | undefined;
  severityTab: 'ALL' | ReconFlagSeverity;
  categoryFilter: string;
  agingFilter: string;
  /**
   * Pre-resolved account IDs from `searchAccounts`.
   * `undefined` = omit filter (search inactive or still loading).
   * `[]` = search active with no matches.
   */
  accountIds?: string[];
};

/**
 * Pure equivalent of the grid filter memo in `useReconFlagsPage` — keep in sync when changing filter logic.
 */
export function buildReconFlagsGridFilter(
  input: BuildReconFlagsGridFilterInput,
): FlagQueryFilterForGrid | null {
  const {
    orgId,
    flagState,
    flagType,
    severityTab,
    categoryFilter,
    agingFilter,
    accountIds,
  } = input;
  if (!orgId) {
    return null;
  }
  const filter: FlagQueryFilterForGrid = {
    organizationId: orgId,
    state: flagState as FlagStates,
  };
  if (flagType) {
    filter.type = flagType as FlagTypes;
  }
  if (severityTab !== 'ALL') {
    filter.priority = severityTab as FlagPriority;
  }
  if (categoryFilter !== 'any') {
    filter.category = categoryFilter as FlagCategory;
  }
  const minAgeDays =
    agingFilter === 'any' ? null : Number.parseInt(agingFilter, 10) || null;
  if (minAgeDays === 1 || minAgeDays === 3 || minAgeDays === 7) {
    filter.firstSeenAtBefore = dayjs()
      .subtract(minAgeDays, 'day')
      .startOf('day')
      .toISOString();
  }
  if (accountIds !== undefined) {
    filter.accountIds = accountIds;
  }
  return filter;
}

export type BuildFetchFlagsGridVariablesInput = {
  gridFilter: FlagQueryFilterForGrid | null;
  pageSize: number;
  page: number;
  flagState: string;
};

/**
 * Variables object for `fetchFlagsForGrid` — pure helper for tests and the page hook.
 */
/**
 * Hide grid rows while account search is pending or the flags query is loading
 * for a new filter. Keeps `previousData` only when paginating within the same filter.
 */
export function shouldSuppressReconFlagsGridResults(
  skipFlagsForAccountSearch: boolean,
  gridLoading: boolean,
  gridFilterKey: string,
  resolvedGridFilterKey: string | null,
): boolean {
  return (
    skipFlagsForAccountSearch ||
    (gridLoading && gridFilterKey !== resolvedGridFilterKey)
  );
}

export function buildFetchFlagsGridVariables(
  input: BuildFetchFlagsGridVariablesInput,
): { input: FetchFlagsInputForGrid } {
  const { gridFilter, pageSize, page, flagState } = input;
  return {
    input: {
      filter: (gridFilter ?? {
        organizationId: '',
        state: flagState as FlagStates,
      }) satisfies FlagQueryFilterForGrid,
      pagination: {
        perPage: pageSize,
        offSet: (page - 1) * pageSize,
      },
    } satisfies FetchFlagsInputForGrid,
  };
}
